import React from 'react';

// Shared schedule editor: the { type, time, days, timezone } form used by both
// the editor's bottom ScheduleBar and the Agents-page settings gear. The browser
// timezone is always folded in so the server cron fires at the correct local time.

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'Mo', tue: 'Tu', wed: 'We', thu: 'Th', fri: 'Fr', sat: 'Sa', sun: 'Su' };

// Detect the user's local timezone once (stable across renders).
export const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export function scheduleSummaryText(schedule = {}) {
  const type = schedule.type || 'off';
  const time = schedule.time || '09:00';
  const days = schedule.days || [];
  const tz = schedule.timezone || BROWSER_TZ;
  if (type === 'off') return null;
  if (type === 'daily') return `Runs daily at ${time} (${tz})`;
  if (type === 'weekly' && days.length > 0) return `Runs ${days.map((d) => DAY_LABELS[d]).join(', ')} at ${time} (${tz})`;
  return null;
}

// Compact variant for the list badges (cards + table rows): "Daily at 09:00" /
// "Mo, We at 09:00". The timezone is appended only when it differs from the
// viewer's — the common same-tz case must stay short enough for nowrap cells.
// Null for off and for a dayless weekly (which the scheduler never fires).
export function scheduleBadgeText(schedule = {}) {
  const type = schedule.type || 'off';
  const time = schedule.time || '09:00';
  const days = schedule.days || [];
  const tz = schedule.timezone && schedule.timezone !== BROWSER_TZ ? ` (${schedule.timezone})` : '';
  if (type === 'daily') return `Daily at ${time}${tz}`;
  if (type === 'weekly' && days.length > 0) return `${days.map((d) => DAY_LABELS[d]).join(', ')} at ${time}${tz}`;
  return null;
}

const s = {
  select: { padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '13px', background: '#fff', cursor: 'pointer' },
  timeInput: { padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '13px', width: '110px' },
  dayBtn: (active) => ({
    padding: '4px 9px', border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 'var(--radius)', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
    background: active ? 'var(--accent-light)' : '#fff',
    color: active ? 'var(--accent)' : 'var(--text-secondary)',
  }),
};

/**
 * @param schedule { type, time, days, timezone }
 * @param onChange (nextSchedule) => void   — always receives the full schedule object
 * @param layout   'inline' (default) | 'stack'
 */
export default function ScheduleFields({ schedule = {}, onChange, layout = 'inline' }) {
  const type = schedule.type || 'off';
  const time = schedule.time || '09:00';
  const days = schedule.days || [];
  const timezone = schedule.timezone || BROWSER_TZ;

  const update = (patch) => onChange({ ...schedule, timezone, ...patch });
  const toggleDay = (day) => {
    const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
    update({ days: next });
  };

  const wrap = layout === 'stack'
    ? { display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }
    : { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' };

  return (
    <div style={wrap}>
      <select style={s.select} value={type} onChange={(e) => update({ type: e.target.value })}>
        <option value="off">Off</option>
        <option value="daily">Every Day</option>
        <option value="weekly">Every Week</option>
      </select>

      {type !== 'off' && (
        <input style={s.timeInput} type="time" value={time} onChange={(e) => update({ time: e.target.value })} />
      )}

      {type === 'weekly' && (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {DAYS.map((d) => (
            <button key={d} type="button" style={s.dayBtn(days.includes(d))} onClick={() => toggleDay(d)}>
              {DAY_LABELS[d]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
